import  { useState, useEffect } from 'react'; 
import { TrendingUp, ArrowRightLeft, Calculator, RefreshCw, Copy, Check, Edit, Globe, Loader, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api/axios';

const ExchangeRatePage = ({ lang }) => {
    const { t } = useTranslation(lang);

    const [activeTab, setActiveTab] = useState('official'); // 'official', 'parallel', 'manage'
    const [officialRates, setOfficialRates] = useState([]);
    const [parallelRates, setParallelRates] = useState([]);
    const [isLoadingRates, setIsLoadingRates] = useState(false);
    const [isSavingRate, setIsSavingRate] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);

    const get_exchanges = process.env.REACT_APP_API_GET_ECHANGES
    const post_exchanges = process.env.REACT_APP_API_POST_ECHANGES
    
    // État pour l'ajout d'un nouveau taux
    const [newRate, setNewRate] = useState({
        currency_from: 'EUR',
        currency_to: 'DZD',
        exchange_rate: '',
        type: '' // 'i' pour informel, vide pour officiel
    });

    // Le reste de votre état existant...
    const [formData, setFormData] = useState({
        amount: '',
        fromCurrency: 'EUR',
        toCurrency: 'DZD'
    });
    const [result, setResult] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    // Devises disponibles
    const currencies = [
        { code: 'EUR', name: 'Euro', symbol: '€' },
        { code: 'USD', name: 'US Dollar', symbol: '$' },
        { code: 'GBP', name: 'British Pound', symbol: '£' },
        { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
        { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
        { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
        { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
        { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
        { code: 'DZD', name: 'Algerian Dinar', symbol: 'DZD' },
        { code: 'TND', name: 'Tunisian Dinar', symbol: 'DT' },
        { code: 'PDS', name: 'Libyan Dinar', symbol: 'LD' }
    ];

    // Charger TOUS les taux depuis l'API
    const fetchAllRates = async () => {
        setIsLoadingRates(true);
        try {
            // Charger les taux officiels
            const officialResponse = await api.get(get_exchanges);
            setOfficialRates(officialResponse.data || []);
            
            // Charger les taux parallèles
            const parallelResponse = await api.get(`${get_exchanges}?type=i`);
            setParallelRates(parallelResponse.data || []);
            console.log(parallelResponse);
            
            
        } catch (error) {
            console.error('Erreur lors du chargement des taux:', error);
            toast.error(t('errorLoadingRates'));
        } finally {
            setIsLoadingRates(false);
        }
    };

    // Charger les taux au montage
    useEffect(() => {
        fetchAllRates();
    }, []);

    // Obtenir le taux courant selon l'onglet actif
    const getCurrentExchangeRate = (fromCurrency, toCurrency) => {
        const rateList = activeTab === 'parallel' ? parallelRates : officialRates;
        
        // Chercher le taux direct
        const directRate = rateList.find(rate => 
            rate.currency_from === fromCurrency && 
            rate.currency_to === toCurrency
        );
        
        if (directRate) {
            return directRate.exchange_rate;
        }
        
        // Chercher via DZD
        const toDzd = rateList.find(rate => 
            rate.currency_from === fromCurrency && 
            rate.currency_to === 'DZD'
        );
        
        const fromDzd = rateList.find(rate => 
            rate.currency_from === 'DZD' && 
            rate.currency_to === toCurrency
        );
        
        if (toDzd && fromDzd) {
            return toDzd.exchange_rate * fromDzd.exchange_rate;
        }
        
        return null;
    };

    // Sauvegarder un nouveau taux
    const saveNewRate = async () => {
        if (!newRate.exchange_rate || isNaN(newRate.exchange_rate)) {
            toast.error(t('invalidRate'));
            return;
        }

        if (newRate.currency_from === newRate.currency_to) {
            toast.error(t('sameCurrency'));
            return;
        }

        setIsSavingRate(true);
        try {
            const payload = {
                currency_from: newRate.currency_from,
                currency_to: newRate.currency_to,
                exchange_rate: parseFloat(newRate.exchange_rate)
            };

            // Ajouter le type seulement pour les taux informels
            if (newRate.type === 'i') {
                payload.type = 'i';
            }

            await api.post(post_exchanges, payload);
            
            toast.success(t('rateSaved'));
            
            // Recharger tous les taux
            await fetchAllRates();
            
            // Réinitialiser le formulaire
            setNewRate({
                currency_from: 'EUR',
                currency_to: 'DZD',
                exchange_rate: '',
                type: newRate.type // Garder le même type
            });
            setShowAddForm(false);
            
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            toast.error(error.response?.data?.message || t('saveError'));
        } finally {
            setIsSavingRate(false);
        }
    };

    // Supprimer un taux
    const deleteExchangeRate = async (rateId, isParallel = false) => {
        if (!window.confirm(t('confirmDelete'))) return;
        
        try {
            await api.delete(`/api/param/currency-exchange/${rateId}`);
            toast.success(t('rateDeleted'));
            
            // Mettre à jour l'état local
            if (isParallel) {
                setParallelRates(prev => prev.filter(rate => rate.id !== rateId));
            } else {
                setOfficialRates(prev => prev.filter(rate => rate.id !== rateId));
            }
            
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            toast.error(t('deleteError'));
        }
    };

    // Vos fonctions existantes (handleChange, swapCurrencies, calculateExchange, resetForm, etc.)
    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const swapCurrencies = () => {
        setFormData(prev => ({
            ...prev,
            fromCurrency: prev.toCurrency,
            toCurrency: prev.fromCurrency
        }));
    };

    const calculateExchange = async (e) => {
        e.preventDefault();

        if (!formData.amount || isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
            toast.error(t('invalidAmount'));
            return;
        }

        if (formData.fromCurrency === formData.toCurrency) {
            toast.error(t('sameCurrency'));
            return;
        }

        setIsLoading(true);

        try {
            const amount = parseFloat(formData.amount);
            const rate = getCurrentExchangeRate(formData.fromCurrency, formData.toCurrency);

            if (!rate) {
                toast.error(t('exchangeRateUnavailable'));
                return;
            }

            const convertedAmount = amount * rate;

            setResult({
                originalAmount: amount,
                fromCurrency: formData.fromCurrency,
                toCurrency: formData.toCurrency,
                exchangeRate: rate,
                convertedAmount: convertedAmount,
                isParallelRate: activeTab === 'parallel'
            });

            toast.success(t('conversionSuccessful'));
        } catch (error) {
            console.error('Erreur de conversion:', error);
            toast.error(t('conversionError'));
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            amount: '',
            fromCurrency: 'EUR',
            toCurrency: 'DZD'
        });
        setResult(null);
        setCopied(false);
    };

    const copyToClipboard = () => {
        if (!result) return;

        const textToCopy = `${getCurrencySymbol(result.toCurrency)}${result.convertedAmount.toFixed(2)}`;

        navigator.clipboard.writeText(textToCopy).then(() => {
            setCopied(true);
            toast.success(t('copiedToClipboard'));
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {
            toast.error(t('copyFailed'));
        });
    };

    const getCurrencySymbol = (code) => {
        return currencies.find(currency => currency.code === code)?.symbol || code;
    };

    // Styles
    const styles = {
         container: {
            padding: '24px',
            background: '#f9fafb',
            minHeight: '100vh'
        },
        header: {
            marginBottom: '8px',
            maxWidth: '600px'
        },
        titleContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '8px'
        },
        title: {
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0
        },
        subtitle: {
            fontSize: '16px',
            color: '#6b7280',
            lineHeight: '1.6',
            margin: 0
        },
        centerContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '70vh'
        },
        card: {
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 4px 25px rgba(0,0,0,0.1)',
            width: '100%',
            maxWidth: '800px'
        },
        form: {
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
        },
        amountGroup: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
        },
        label: {
            fontWeight: '700',
            color: '#374151',
            fontSize: '20px',
            marginBottom: '8px'
        },
        amountInput: {
            width: '100%',
            padding: '16px',
            border: '2px solid #7c3aed',
            borderRadius: '12px',
            fontSize: '18px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box',
            background: 'white'
        },
        amountInputFocus: {
            borderColor: '#7c3aed',
            boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)'
        },
        currenciesGroup: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            justifyContent: 'space-between'
        },
        currencySelect: {
            flex: 1,
            padding: '14px 16px',
            border: '2px solid #e5e7eb',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '500',
            background: 'white',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            minWidth: 0,
        },
        swapButton: {
            padding: '12px',
            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            flexShrink: 0
        },
        buttonGroup: {
            display: 'flex',
            gap: '12px',
            marginTop: '0px'
        },
        primaryButton: {
            flex: 1,
            padding: '16px 10px',
            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s ease'
        },
        secondaryButton: {
            padding: '16px 24px',
            background: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s ease'
        },
        resultCard: {
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            borderRadius: '12px',
            padding: '24px',
            marginTop: '8px',
            border: '1px solid #bae6fd',
            position: 'relative'
        },
        resultTitle: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#1e40af',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        exchangeRate: {
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '14px'
        },
        conversionResult: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#1f2937'
        },
        conversionContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            justifyContent: 'center', 
            marginBottom: '8px', 
        },
        convertedGroup: {
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            backgroundColor: '#c5d5f3ff', 
            borderRadius: '8px',
            padding: '4px 8px', 
            border: '1px solid #e5e7eb',
        },
        copyButton: {
            padding: '4px', 
            background: 'transparent', 
            border: 'none', 
            cursor: 'pointer',
            color: '#6b7280',
            display: 'flex',
            alignSelf: 'flex-start'
        },
        convertedAmount: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#1f2937',
        },
        loadingSpinner: {
            animation: 'spin 1s linear infinite'
        },
        rateDisplay: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            fontSize: '14px',
            color: '#6b7280',
            padding: '8px',
            background: '#f8fafc',
            borderRadius: '8px',
            marginTop: '0px'
        },
        // Styles pour les onglets
        tabsContainer: {
            display: 'flex',
            gap: '8px',
            marginBottom: '24px',
            borderBottom: '1px solid #e5e7eb',
            paddingBottom: '8px'
        },
        tab: {
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            color: '#6b7280',
            minWidth: '50px'
        },
        activeTab: {
            background: '#7c3aed',
            color: 'white'
        },
        manageRatesContainer: {
            background: '#f8fafc',
            borderRadius: '12px',
            padding: '24px',
            marginTop: '24px'
        },
        rateForm: {
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            marginBottom: '20px'
        },
        rateInput: {
            padding: '12px',
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px',
            flex: 1
        },
        savedRatesList: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
        },
        savedRateItem: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px',
            background: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
        },
        ratesLoading: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
            color: '#6b7280'
        },
        rateActions: {
            display: 'flex',
            gap: '8px'
        },
        editButton: {
            padding: '6px 12px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px'
        },
        rateFormContainer: {
            background: '#f8fafc',
            padding: '6px',
            borderRadius: '12px',
            marginBottom: '24px'
        },
        ratesSection: {
            marginTop: '32px'
        },
        ratesHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
        },
        ratesTitle: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937'
        },
        addButton: {
            padding: '8px 16px',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px'
        },
        ratesList: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            maxHeight: '400px',
            overflowY: 'auto'
        },
        rateItem: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            transition: 'all 0.2s ease'
        },
        rateInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        },
        rateConversion: {
            fontSize: '16px',
            fontWeight: '500',
            color: '#1f2937'
        },
        rateValue: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#7c3aed',
            margin: '0 4px'
        },
        rateBadge: {
            padding: '2px 8px',
            background: '#10b981',
            color: 'white',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500'
        },
        officialBadge: {
            padding: '2px 8px',
            background: '#7c3aed',
            color: 'white',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500'
        },
        deleteButton: {
            padding: '6px',
            background: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        addForm: {
            background: '#f8fafc',
            padding: '10px',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid #e5e7eb'
        },
        formRow: {
            display: 'flex',
            gap: '6px',
            alignItems: 'center',
            marginBottom: '16px',
            flexwrap: 'wrap'
        },
        formInput: {
            flex: 1,
            padding: '12px 6px',
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px',
            minWidth: '10px'
        },
        formActions: {
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
        },
        cancelButton: {
            padding: '10px 20px',
            background: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
        },
        saveButton: {
            padding: '10px 20px',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px',
            color: '#6b7280'
        },
        manageContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: '32px'
        },
        manageSection: {
            background: '#f8fafc',
            borderRadius: '12px',
            padding: '24px'
        },
        combinedCopyButtonStyle:{
            background: '#f8fafc',
        }
    };

    // Rendu de la liste des taux
    const renderRatesList = (rates, isParallel = false) => {
        if (isLoadingRates) {
            return (
                <div style={styles.loadingContainer}>
                    <Loader size={20} style={{ animation: 'spin 1s linear infinite', marginRight: '10px' }} />
                    {t('loading')}...
                </div>
            );
        }

        if (rates.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                    {t('noRatesAvailable')}
                </div>
            );
        }

        return (
            <div style={styles.ratesList}>
                {rates.map(rate => (
                    <div key={rate.id} style={styles.rateItem}>
                        <div style={styles.rateInfo}>
                            <div style={styles.rateConversion}>
                                1 <strong>{rate.currency_from}</strong> = 
                                <span style={styles.rateValue}>{rate.exchange_rate.toFixed(4)}</span>
                                <strong>{rate.currency_to}</strong>
                            </div>
                            <span style={isParallel ? styles.rateBadge : styles.officialBadge}>
                                {isParallel ? t('parallel') : t('official')}
                            </span>
                        </div>
                        <button
                            style={styles.deleteButton}
                            onClick={() => deleteExchangeRate(rate.id, isParallel)}
                            title={t('delete')}
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                ))}
            </div>
        );
    };

    // Rendu du formulaire d'ajout
    const renderAddForm = () => {
        if (!showAddForm) return null;

        return (
            <div style={styles.addForm}>
                <h4 style={{ marginBottom: '16px', color: '#1f2937' }}>
                    {t('addNewRate')}
                </h4>
                <div style={styles.formRow}>
                    <select
                        value={newRate.currency_from}
                        onChange={(e) => setNewRate(prev => ({ 
                            ...prev, 
                            currency_from: e.target.value 
                        }))}
                        style={styles.formInput}
                    >
                        {currencies.map(currency => (
                            <option key={currency.code} value={currency.code}>
                                {currency.code} - {currency.name}
                            </option>
                        ))}
                    </select>
                    
                    <span>{t('to')}</span>
                    
                    <select
                        value={newRate.currency_to}
                        onChange={(e) => setNewRate(prev => ({ 
                            ...prev, 
                            currency_to: e.target.value 
                        }))}
                        style={styles.formInput}
                    >
                        {currencies.map(currency => (
                            <option key={currency.code} value={currency.code}>
                                {currency.code} - {currency.name}
                            </option>
                        ))}
                    </select>
                    
                    <input
                        type="number"
                        step="0.0001"
                        value={newRate.exchange_rate}
                        onChange={(e) => setNewRate(prev => ({ 
                            ...prev, 
                            exchange_rate: e.target.value 
                        }))}
                        placeholder={t('exchangeRate')}
                        style={styles.formInput}
                    />
                    
                    {activeTab === 'manage' && (
                        <select
                            value={newRate.type}
                            onChange={(e) => setNewRate(prev => ({ 
                                ...prev, 
                                type: e.target.value 
                            }))}
                            style={styles.formInput}
                        >
                            <option value="">{t('official')}</option>
                            <option value="i">{t('parallel')}</option>
                        </select>
                    )}
                </div>
                <div style={styles.formActions}>
                    <button
                        style={styles.cancelButton}
                        onClick={() => {
                            setShowAddForm(false);
                            setNewRate({
                                currency_from: 'EUR',
                                currency_to: 'DZD',
                                exchange_rate: '',
                                type: ''
                            });
                        }}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        style={styles.saveButton}
                        onClick={saveNewRate}
                        disabled={isSavingRate}
                    >
                        {isSavingRate ? (
                            <>
                                <Loader size={16} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                                {t('saving')}...
                            </>
                        ) : (
                            t('save')
                        )}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div style={styles.container}>
            {/* Header avec titre et onglets */}
            <div style={styles.header}>
                <div style={styles.titleContainer}>
                    <TrendingUp size={36} color="#7c3aed" />
                    <h1 style={styles.title}>{t('exchangeRate')}</h1>
                </div>
                
                {/* Navigation par onglets */}
                <div style={styles.tabsContainer}>
                    <button
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'official' ? styles.activeTab : {})
                        }}
                        onClick={() => {
                            setActiveTab('official');
                            resetForm();
                        }}
                    >
                        <Globe size={16} />
                        {t('officialRate')}
                        <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                            ({officialRates.length})
                        </span>
                    </button>
                    
                    <button
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'parallel' ? styles.activeTab : {})
                        }}
                        onClick={() => {
                            setActiveTab('parallel');
                            resetForm();
                        }}
                    >
                        <Edit size={16} />
                        {t('parallelRate')}
                        <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                            ({parallelRates.length})
                        </span>
                    </button>
                    
                    <button
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'manage' ? styles.activeTab : {})
                        }}
                        onClick={() => {
                            setActiveTab('manage');
                            setShowAddForm(true);
                        }}
                    >
                        <Calculator size={16} />
                        {t('manageRates')}
                        <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                            ({officialRates.length + parallelRates.length})
                        </span>
                    </button>
                </div>
            </div>

            {/* Contenu selon l'onglet actif */}
            {activeTab === 'manage' ? (
                <div style={styles.centerContainer}>
                    <div style={styles.card}>

                        
                        {/* Formulaire d'ajout */}
                        {renderAddForm()}
                        
                        {/* Sections des taux */}
                        <div style={styles.manageContainer}>
                            {/* Taux Officiels */}
                            <div style={styles.manageSection}>
                                <div style={styles.ratesHeader}>
                                    <h4 style={styles.ratesTitle}>
                                        {t('officialRates')} ({officialRates.length})
                                    </h4>
                                </div>
                                {renderRatesList(officialRates, false)}
                            </div>
                            
                            {/* Taux Parallèles */}
                            <div style={styles.manageSection}>
                                <div style={styles.ratesHeader}>
                                    <h4 style={styles.ratesTitle}>
                                        {t('parallelRates')} ({parallelRates.length})
                                    </h4>
                                </div>
                                {renderRatesList(parallelRates, true)}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                /* Onglets Officiel ou Parallèle avec conversion + liste */
                <div style={styles.centerContainer}>
                    <div style={styles.card}>
                        {/* Bannière d'avertissement pour parallèle */}
                        {activeTab === 'parallel' && (
                            <div style={{
                                background: '#fef3c7',
                                border: '1px solid #fbbf24',
                                borderRadius: '8px',
                                padding: '12px',
                                marginBottom: '20px',
                                color: '#92400e'
                            }}>
                                ⚠️ {t('usingParallelRates')}
                            </div>
                        )}
                        
                        {/* Module de conversion */}
                        <form onSubmit={calculateExchange} style={styles.form}>
                            {/* Montant */}
                            <div style={styles.amountGroup}>
                                <label style={styles.label}>{t('amount')}</label>
                                <input
                                    type="number"
                                    value={formData.amount}
                                    step={0.01}
                                    onChange={(e) => handleChange('amount', e.target.value)}
                                    style={{
                                        ...styles.amountInput,
                                        ...(isFocused ? styles.amountInputFocus : {})
                                    }}
                                    placeholder={t('enterAmount')}
                                    onFocus={() => setIsFocused(true)}
                                    onBlur={() => setIsFocused(false)}
                                    required
                                />
                            </div>

                            {/* Devises */}
                            <div style={styles.currenciesGroup}>
                                <select
                                    value={formData.fromCurrency}
                                    onChange={(e) => handleChange('fromCurrency', e.target.value)}
                                    style={styles.currencySelect}
                                >
                                    {currencies.map(currency => (
                                        <option key={currency.code} value={currency.code}>
                                            {currency.code} - {currency.name}
                                        </option>
                                    ))}
                                </select>

                                <button
                                    type="button"
                                    style={styles.swapButton}
                                    onClick={swapCurrencies}
                                    onMouseEnter={(e) => e.target.style.transform = 'rotate(180deg)'}
                                    onMouseLeave={(e) => e.target.style.transform = 'rotate(0deg)'}
                                >
                                    <ArrowRightLeft size={20} />
                                </button>

                                <select
                                    value={formData.toCurrency}
                                    onChange={(e) => handleChange('toCurrency', e.target.value)}
                                    style={styles.currencySelect}
                                >
                                    {currencies.map(currency => (
                                        <option key={currency.code} value={currency.code}>
                                            {currency.code} - {currency.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Affichage du taux */}
                            {getCurrentExchangeRate(formData.fromCurrency, formData.toCurrency) && (
                                <div style={styles.rateDisplay}>
                                    <TrendingUp size={14} />
                                    1 {formData.fromCurrency} = {getCurrentExchangeRate(formData.fromCurrency, formData.toCurrency).toFixed(4)} {formData.toCurrency}
                                    {activeTab === 'parallel' && (
                                        <span style={{
                                            marginLeft: '8px',
                                            color: '#10b981',
                                            fontWeight: '600',
                                            fontSize: '12px'
                                        }}>
                                            ({t('parallel')})
                                        </span>
                                    )}
                                </div>
                            )}
                            
                            {/* Boutons de conversion */}
                            <div style={styles.buttonGroup}>
                                <button
                                    type="submit"
                                    style={styles.primaryButton}
                                    disabled={isLoading || isLoadingRates}
                                >
                                    {isLoading ? (
                                        <>
                                            <RefreshCw size={18} style={styles.loadingSpinner} />
                                            {t('calculating')}
                                        </>
                                    ) : (
                                        <>
                                            <Calculator size={18} />
                                            {t('calculate')}
                                        </>
                                    )}
                                </button>

                                <button
                                    type="button"
                                    style={styles.secondaryButton}
                                    onClick={resetForm}
                                >
                                    <RefreshCw size={18} />
                                    {t('reset')}
                                </button>
                            </div>
                        </form>

                        {/* Résultat de conversion */}
                        {result && (
                            <div style={styles.resultCard}>
                                <div style={styles.resultTitle}>
                                    <TrendingUp size={20} />
                                    {t('conversionResult')}
                                    {result.isParallelRate && (
                                        <span style={styles.rateBadge}>
                                            {t('parallel')}
                                        </span>
                                    )}
                                </div>
                                <div style={styles.conversionContainer}>
                                    <div style={styles.conversionResult}>
                                        {getCurrencySymbol(result.fromCurrency)}{result.originalAmount.toFixed(2)} =
                                    </div>
                                    
                                    <div style={styles.convertedGroup}>
                                        <span style={styles.convertedAmount}>
                                            {getCurrencySymbol(result.toCurrency)}{result.convertedAmount.toFixed(2)}
                                        </span>
                                        
                                        <button
                                            style={styles.combinedCopyButtonStyle} 
                                            onClick={copyToClipboard}
                                            title={t('copyToClipboard')}
                                        >
                                            {copied ? <Check size={16} color="#10b981" /> : <Copy size={16} />} 
                                        </button>
                                    </div>
                                </div>
                                <div style={styles.exchangeRate}>
                                    1 {result.fromCurrency} = {result.exchangeRate.toFixed(4)} {result.toCurrency}
                                </div>
                            </div>
                        )}
                        
                        {/* Liste des taux dans l'onglet */}
                        <div style={styles.ratesSection}>
                            <div style={styles.ratesHeader}>
                                <h4 style={styles.ratesTitle}>
                                    {activeTab === 'parallel' ? t('parallelRates') : t('officialRates')}
                                    <span style={{ marginLeft: '8px', fontSize: '14px', color: '#6b7280' }}>
                                        ({activeTab === 'parallel' ? parallelRates.length : officialRates.length})
                                    </span>
                                </h4>
                                <button
                                    style={styles.addButton}
                                    onClick={() => {
                                        setShowAddForm(true);
                                        setNewRate(prev => ({
                                            ...prev,
                                            type: activeTab === 'parallel' ? 'i' : ''
                                        }));
                                    }}
                                >
                                    <Plus size={16} />
                                    {t('addRate')}
                                </button>
                            </div>
                            
                            {renderAddForm()}
                            {renderRatesList(activeTab === 'parallel' ? parallelRates : officialRates, activeTab === 'parallel')}
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="bottom-right" />
        </div>
    );
};

export default ExchangeRatePage;